import React, { useState } from 'react';
import {
    Clock, Plus, Edit2, Trash2, X, Check, Loader2, Globe, Calendar,
    ChevronDown, ToggleLeft, ToggleRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createSchedule, updateSchedule, deleteSchedule, addDateOverride, deleteDateOverride } from '../api/calendar';
import ConfirmModal from './ConfirmModal';

const DAYS_OF_WEEK = [
    { id: 0, label: 'Sunday', short: 'Sun' },
    { id: 1, label: 'Monday', short: 'Mon' },
    { id: 2, label: 'Tuesday', short: 'Tue' },
    { id: 3, label: 'Wednesday', short: 'Wed' },
    { id: 4, label: 'Thursday', short: 'Thu' },
    { id: 5, label: 'Friday', short: 'Fri' },
    { id: 6, label: 'Saturday', short: 'Sat' }
];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        TIME_OPTIONS.push(`${hour}:${minute}`);
    }
}

const TIMEZONE_OPTIONS = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
];

const AvailabilitySchedule = ({
    schedules = [],
    onCreateSchedule,
    onEditSchedule,
    onScheduleSaved,
    onScheduleDeleted,
    isEditorOpen,
    setIsEditorOpen,
    editingSchedule,
    setEditingSchedule
}) => {
    // Local state for schedule editor
    const [formData, setFormData] = useState({
        name: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isDefault: false,
        availability: DAYS_OF_WEEK.slice(1, 6).map(day => ({
            day: day.id,
            start_time: '09:00',
            end_time: '17:00',
            enabled: true
        }))
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [errors, setErrors] = useState({});
    const [expandedSchedule, setExpandedSchedule] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, scheduleId: null });

    // Date override state
    const [showOverrideForm, setShowOverrideForm] = useState(null);
    const [overrideDate, setOverrideDate] = useState('');
    const [overrideReason, setOverrideReason] = useState('');

    // Initialize form when editing
    React.useEffect(() => {
        if (editingSchedule) {
            const availabilityMap = {};
            (editingSchedule.availability || []).forEach(av => {
                availabilityMap[av.day] = av;
            });

            setFormData({
                name: editingSchedule.name || '',
                timezone: editingSchedule.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                isDefault: editingSchedule.is_default || false,
                availability: DAYS_OF_WEEK.map(day => ({
                    day: day.id,
                    start_time: availabilityMap[day.id]?.start_time || '09:00',
                    end_time: availabilityMap[day.id]?.end_time || '17:00',
                    enabled: !!availabilityMap[day.id]
                }))
            });
        } else {
            // Reset to defaults
            setFormData({
                name: '',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                isDefault: schedules.length === 0,
                availability: DAYS_OF_WEEK.map(day => ({
                    day: day.id,
                    start_time: '09:00',
                    end_time: '17:00',
                    enabled: day.id >= 1 && day.id <= 5 // Mon-Fri
                }))
            });
        }
        setErrors({});
    }, [editingSchedule, schedules.length]);

    const handleDayToggle = (dayId) => {
        setFormData(prev => ({
            ...prev,
            availability: prev.availability.map(av =>
                av.day === dayId ? { ...av, enabled: !av.enabled } : av
            )
        }));
    };

    const handleTimeChange = (dayId, field, value) => {
        setFormData(prev => ({
            ...prev,
            availability: prev.availability.map(av =>
                av.day === dayId ? { ...av, [field]: value } : av
            )
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Schedule name is required';
        }
        const enabledDays = formData.availability.filter(av => av.enabled);
        if (enabledDays.length === 0) {
            newErrors.availability = 'At least one day must be enabled';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSaving(true);
        try {
            const payload = {
                name: formData.name.trim(),
                timezone: formData.timezone,
                is_default: formData.isDefault,
                availability: formData.availability
                    .filter(av => av.enabled)
                    .map(av => ({
                        day: av.day,
                        start_time: av.start_time,
                        end_time: av.end_time
                    }))
            };

            let result;
            if (editingSchedule?.id) {
                result = await updateSchedule(editingSchedule.id, payload);
            } else {
                result = await createSchedule(payload);
            }

            onScheduleSaved?.(result.data);
            setIsEditorOpen(false);
            setEditingSchedule(null);
        } catch (err) {
            console.error('Failed to save schedule:', err);
            setErrors({ submit: err.message });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (scheduleId) => {
        setDeleteConfirm({ isOpen: true, scheduleId });
    };

    const handleConfirmDelete = async () => {
        const scheduleId = deleteConfirm.scheduleId;
        if (!scheduleId) return;

        setIsDeleting(true);
        try {
            await deleteSchedule(scheduleId);
            onScheduleDeleted?.(scheduleId);
            setDeleteConfirm({ isOpen: false, scheduleId: null });
        } catch (err) {
            console.error('Failed to delete schedule:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleAddOverride = async (scheduleId) => {
        if (!overrideDate) return;

        try {
            const result = await addDateOverride(scheduleId, {
                date: overrideDate,
                isUnavailable: true,
                reason: overrideReason
            });

            // Update local state
            const updatedSchedules = schedules.map(s => {
                if (s.id === scheduleId) {
                    return {
                        ...s,
                        date_overrides: [...(s.date_overrides || []), result.data]
                    };
                }
                return s;
            });

            onScheduleSaved?.(updatedSchedules.find(s => s.id === scheduleId));
            setShowOverrideForm(null);
            setOverrideDate('');
            setOverrideReason('');
        } catch (err) {
            console.error('Failed to add date override:', err);
            alert('Failed to add date override: ' + err.message);
        }
    };

    const handleRemoveOverride = async (scheduleId, overrideId) => {
        try {
            await deleteDateOverride(scheduleId, overrideId);

            // Update local state
            const updatedSchedules = schedules.map(s => {
                if (s.id === scheduleId) {
                    return {
                        ...s,
                        date_overrides: (s.date_overrides || []).filter(o => o.id !== overrideId)
                    };
                }
                return s;
            });

            onScheduleSaved?.(updatedSchedules.find(s => s.id === scheduleId));
        } catch (err) {
            console.error('Failed to remove date override:', err);
            alert('Failed to remove date override: ' + err.message);
        }
    };

    const formatTime12 = (time24) => {
        const [hours, minutes] = time24.split(':');
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${minutes} ${ampm}`;
    };

    // Empty state
    if (schedules.length === 0 && !isEditorOpen) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <div className="p-6 rounded-2xl dark:bg-slate-800/50 bg-slate-100 mb-4">
                    <Clock size={48} className="dark:text-slate-600 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
                    No availability schedules
                </h3>
                <p className="text-sm dark:text-slate-400 text-slate-500 mb-6 text-center max-w-md">
                    Create a schedule to define when you're available for bookings.
                </p>
                <button
                    onClick={onCreateSchedule}
                    className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105"
                >
                    <Plus size={18} />
                    Create Schedule
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Schedule List */}
            {!isEditorOpen && (
                <div className="space-y-4">
                    {schedules.map((schedule) => (
                        <motion.div
                            key={schedule.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="rounded-xl border dark:bg-slate-900/50 bg-white dark:border-slate-800 border-slate-200 overflow-hidden"
                        >
                            {/* Schedule Header */}
                            <div
                                onClick={() => setExpandedSchedule(expandedSchedule === schedule.id ? null : schedule.id)}
                                className="p-4 flex items-center justify-between cursor-pointer dark:hover:bg-slate-800/50 hover:bg-slate-50 transition"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg dark:bg-slate-800 bg-slate-100">
                                        <Clock size={18} className="text-sky-500" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold dark:text-white text-slate-900">
                                                {schedule.name}
                                            </h3>
                                            {schedule.is_default && (
                                                <span className="px-2 py-0.5 text-xs font-medium bg-sky-500/10 text-sky-500 rounded-full">
                                                    Default
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500 mt-1">
                                            <Globe size={12} />
                                            <span>{schedule.timezone}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEditSchedule(schedule);
                                        }}
                                        className="p-2 rounded-lg dark:text-slate-400 text-slate-500 dark:hover:bg-slate-700 hover:bg-slate-200 transition"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClick(schedule.id);
                                        }}
                                        className="p-2 rounded-lg text-red-500 hover:bg-red-500/10 transition"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    <ChevronDown
                                        size={18}
                                        className={`dark:text-slate-400 text-slate-500 transition-transform ${expandedSchedule === schedule.id ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            </div>

                            {/* Expanded Details */}
                            <AnimatePresence>
                                {expandedSchedule === schedule.id && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="border-t dark:border-slate-800 border-slate-200 overflow-hidden"
                                    >
                                        <div className="p-4 space-y-4">
                                            {/* Weekly Hours */}
                                            <div>
                                                <h4 className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-3">
                                                    Weekly Hours
                                                </h4>
                                                <div className="space-y-2">
                                                    {DAYS_OF_WEEK.map(day => {
                                                        const dayAvail = (schedule.availability || []).find(a => a.day === day.id);
                                                        return (
                                                            <div key={day.id} className="flex items-center gap-4">
                                                                <span className="w-24 text-sm dark:text-slate-400 text-slate-500">
                                                                    {day.label}
                                                                </span>
                                                                {dayAvail ? (
                                                                    <span className="text-sm dark:text-white text-slate-900">
                                                                        {formatTime12(dayAvail.start_time)} - {formatTime12(dayAvail.end_time)}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-sm dark:text-slate-600 text-slate-400">
                                                                        Unavailable
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Date Overrides */}
                                            <div>
                                                <div className="flex items-center justify-between mb-3">
                                                    <h4 className="text-sm font-medium dark:text-slate-300 text-slate-700">
                                                        Date Overrides
                                                    </h4>
                                                    <button
                                                        onClick={() => setShowOverrideForm(schedule.id)}
                                                        className="text-sm text-sky-500 hover:text-sky-400 flex items-center gap-1"
                                                    >
                                                        <Plus size={14} />
                                                        Add Override
                                                    </button>
                                                </div>

                                                {/* Override Form */}
                                                {showOverrideForm === schedule.id && (
                                                    <div className="p-3 rounded-lg dark:bg-slate-800 bg-slate-50 mb-3 space-y-3">
                                                        <div>
                                                            <label className="block text-xs font-medium dark:text-slate-400 text-slate-500 mb-1">
                                                                Date
                                                            </label>
                                                            <input
                                                                type="date"
                                                                value={overrideDate}
                                                                onChange={(e) => setOverrideDate(e.target.value)}
                                                                min={new Date().toISOString().split('T')[0]}
                                                                className="w-full px-3 py-1.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium dark:text-slate-400 text-slate-500 mb-1">
                                                                Reason (optional)
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={overrideReason}
                                                                onChange={(e) => setOverrideReason(e.target.value)}
                                                                placeholder="e.g., Holiday, Vacation"
                                                                className="w-full px-3 py-1.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleAddOverride(schedule.id)}
                                                                disabled={!overrideDate}
                                                                className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:bg-sky-600/50 text-white text-sm rounded-lg transition"
                                                            >
                                                                Add
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setShowOverrideForm(null);
                                                                    setOverrideDate('');
                                                                    setOverrideReason('');
                                                                }}
                                                                className="px-3 py-1.5 dark:text-slate-400 text-slate-500 text-sm rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Override List */}
                                                {(schedule.date_overrides || []).length > 0 ? (
                                                    <div className="space-y-2">
                                                        {schedule.date_overrides.map(override => (
                                                            <div
                                                                key={override.id}
                                                                className="flex items-center justify-between p-2 rounded-lg dark:bg-slate-800/50 bg-slate-50"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <Calendar size={14} className="text-red-500" />
                                                                    <span className="text-sm dark:text-white text-slate-900">
                                                                        {new Date(override.date).toLocaleDateString('en-US', {
                                                                            weekday: 'short',
                                                                            month: 'short',
                                                                            day: 'numeric'
                                                                        })}
                                                                    </span>
                                                                    {override.reason && (
                                                                        <span className="text-xs dark:text-slate-500 text-slate-400">
                                                                            ({override.reason})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <button
                                                                    onClick={() => handleRemoveOverride(schedule.id, override.id)}
                                                                    className="p-1 text-red-500 hover:bg-red-500/10 rounded transition"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm dark:text-slate-500 text-slate-400">
                                                        No date overrides set
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Schedule Editor */}
            <AnimatePresence>
                {isEditorOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="rounded-xl border dark:bg-slate-900/50 bg-white dark:border-slate-800 border-slate-200 p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold dark:text-white text-slate-900">
                                {editingSchedule ? 'Edit Schedule' : 'New Schedule'}
                            </h3>
                            <button
                                onClick={() => {
                                    setIsEditorOpen(false);
                                    setEditingSchedule(null);
                                }}
                                className="p-2 rounded-lg dark:text-slate-400 text-slate-500 dark:hover:bg-slate-800 hover:bg-slate-100 transition"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Name & Timezone */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                        Schedule Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="e.g., Working Hours"
                                        className={`w-full px-4 py-2.5 rounded-lg border dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition ${
                                            errors.name ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                                        }`}
                                    />
                                    {errors.name && (
                                        <p className="text-sm text-red-500 mt-1">{errors.name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                        Timezone
                                    </label>
                                    <select
                                        value={formData.timezone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-800 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500/50 transition"
                                    >
                                        {TIMEZONE_OPTIONS.map(tz => (
                                            <option key={tz} value={tz}>{tz}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Weekly Availability */}
                            <div>
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-3">
                                    Weekly Availability
                                </label>
                                <div className="space-y-3">
                                    {DAYS_OF_WEEK.map(day => {
                                        const dayData = formData.availability.find(a => a.day === day.id);
                                        return (
                                            <div
                                                key={day.id}
                                                className="flex items-center gap-4 p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleDayToggle(day.id)}
                                                    className="flex items-center gap-2"
                                                >
                                                    {dayData?.enabled ? (
                                                        <ToggleRight size={24} className="text-sky-500" />
                                                    ) : (
                                                        <ToggleLeft size={24} className="dark:text-slate-500 text-slate-400" />
                                                    )}
                                                </button>

                                                <span className={`w-24 text-sm font-medium ${
                                                    dayData?.enabled ? 'dark:text-white text-slate-900' : 'dark:text-slate-500 text-slate-400'
                                                }`}>
                                                    {day.label}
                                                </span>

                                                {dayData?.enabled && (
                                                    <div className="flex items-center gap-2">
                                                        <select
                                                            value={dayData.start_time}
                                                            onChange={(e) => handleTimeChange(day.id, 'start_time', e.target.value)}
                                                            className="px-3 py-1.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                                                        >
                                                            {TIME_OPTIONS.map(time => (
                                                                <option key={time} value={time}>{formatTime12(time)}</option>
                                                            ))}
                                                        </select>
                                                        <span className="dark:text-slate-500 text-slate-400">to</span>
                                                        <select
                                                            value={dayData.end_time}
                                                            onChange={(e) => handleTimeChange(day.id, 'end_time', e.target.value)}
                                                            className="px-3 py-1.5 rounded-lg border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                                                        >
                                                            {TIME_OPTIONS.map(time => (
                                                                <option key={time} value={time}>{formatTime12(time)}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {errors.availability && (
                                    <p className="text-sm text-red-500 mt-2">{errors.availability}</p>
                                )}
                            </div>

                            {/* Default Schedule */}
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.isDefault}
                                    onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                                    className="w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500"
                                />
                                <div>
                                    <p className="text-sm font-medium dark:text-white text-slate-900">Set as default schedule</p>
                                    <p className="text-xs dark:text-slate-400 text-slate-500">
                                        This schedule will be used for new event types
                                    </p>
                                </div>
                            </label>

                            {/* Error Message */}
                            {errors.submit && (
                                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                                    <p className="text-sm text-red-500">{errors.submit}</p>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3 pt-4 border-t dark:border-slate-800 border-slate-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditorOpen(false);
                                        setEditingSchedule(null);
                                    }}
                                    className="px-4 py-2 rounded-lg dark:text-slate-300 text-slate-600 dark:hover:bg-slate-800 hover:bg-slate-100 font-medium transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="px-6 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-sky-600/50 text-white rounded-lg font-medium flex items-center gap-2 transition"
                                >
                                    {isSaving && <Loader2 size={16} className="animate-spin" />}
                                    {editingSchedule ? 'Save Changes' : 'Create Schedule'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Schedule Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, scheduleId: null })}
                onConfirm={handleConfirmDelete}
                title="Delete Schedule"
                message="Are you sure you want to delete this availability schedule? This action cannot be undone and may affect your event types using this schedule."
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default AvailabilitySchedule;
